// Chat spam protection: one shared gate for public chat, guild chat and private messages.
//
// Normal chatting never feels it: one message per 1.5 s as before, and the rest
// only bites on spam.
// - The same message a third time within a minute is refused ("lol" twice is fine).
// - Flooding (10 messages inside 20 s, about the fastest the 1.5 s wait allows) pauses
//   chat for 15 s, then 30 s, 1 min and 5 min for repeat floods; the count resets after
//   15 quiet minutes.
// - Shouting is calmed rather than refused: a long line in capitals goes out in lower
//   case, and a letter or symbol held down ("aaaaaaaa", "!!!!!!!!") is cut to five.

export const CHAT_GAP_MS = 1500;
export const SPAM = {
  repeatMs: 60_000,
  /** Copies of one message allowed inside repeatMs. */
  repeats: 2,
  burstMs: 20_000,
  burstMax: 10,
  /** Pause lengths for the first, second, third and later floods. */
  mutesMs: [15_000, 30_000, 60_000, 300_000],
  forgiveMs: 15 * 60_000,
  /** Lines with at least this many letters are checked for capitals. */
  capsMin: 8,
  capsShare: 0.7,
  runMax: 5,
} as const;

export interface SpamState {
  /** Recent messages: when, and the text reduced to letters and digits. */
  recent: { ts: number; key: string }[];
  strikes: number;
  strikeAt: number;
  mutedUntil: number;
}

/** Who is sending: the online player's chat clock and spam record. */
export interface ChatSender { lastChat: number; spam?: SpamState }

export type ChatGate = { ok: true; text: string } | { ok: false; why: string; /** Just the 1.5 s wait: chat drops these quietly. */ tooSoon?: boolean };

const secs = (ms: number) => (ms >= 60_000 ? `${Math.ceil(ms / 60_000)} min` : `${Math.ceil(ms / 1000)} s`);

/** Why chat is paused for this sender right now, or null. */
export function chatPaused(s: ChatSender, now: number): string | null {
  const left = (s.spam?.mutedUntil ?? 0) - now;
  return left > 0 ? `Chat is paused for ${secs(left)}: too many messages.` : null;
}

/** A long line in capitals in lower case, and any character held down cut to five. */
export function calmText(text: string): string {
  let out = text.replace(/(.)\1{5,}/gsu, (run) => [...run].slice(0, SPAM.runMax).join(''));
  const letters = out.match(/\p{L}/gu) ?? [];
  const upper = out.match(/\p{Lu}/gu) ?? [];
  if (letters.length >= SPAM.capsMin && upper.length / letters.length > SPAM.capsShare) {
    const low = out.toLowerCase();
    out = low.replace(/\p{L}/u, (ch) => ch.toUpperCase());
  }
  return out;
}

const keyOf = (text: string) => text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

/**
 * Let one chat line or private message through, or say why not. On success the sender's
 * clock and record are updated and the text to send comes back (calmed, see calmText).
 */
export function chatGate(s: ChatSender, raw: string, now: number): ChatGate {
  const paused = chatPaused(s, now);
  if (paused) return { ok: false, why: paused };
  if (now - s.lastChat < CHAT_GAP_MS) return { ok: false, why: 'One message at a time: try again in a moment.', tooSoon: true };
  const sp = (s.spam ??= { recent: [], strikes: 0, strikeAt: 0, mutedUntil: 0 });
  sp.recent = sp.recent.filter((r) => now - r.ts < Math.max(SPAM.repeatMs, SPAM.burstMs));
  if (sp.strikes && now - sp.strikeAt > SPAM.forgiveMs) sp.strikes = 0;
  const text = calmText(raw);
  const key = keyOf(text) || text;
  if (sp.recent.filter((r) => r.key === key && now - r.ts < SPAM.repeatMs).length >= SPAM.repeats) return { ok: false, why: 'You just said that.' };
  if (sp.recent.filter((r) => now - r.ts < SPAM.burstMs).length >= SPAM.burstMax - 1) {
    const ms = SPAM.mutesMs[Math.min(sp.strikes, SPAM.mutesMs.length - 1)];
    sp.strikes++; sp.strikeAt = now; sp.mutedUntil = now + ms; sp.recent = [];
    return { ok: false, why: `Chat is paused for ${secs(ms)}: too many messages.` };
  }
  sp.recent.push({ ts: now, key });
  s.lastChat = now;
  return { ok: true, text };
}
